import { useEffect, useMemo, useState } from "react";
import {
  Inbox, MessageSquare, Sparkles, BookOpen, CheckCircle2, XCircle,
  MoreHorizontal, Plus, ArrowRight, Mail, Phone, Send, Trash2,
} from "lucide-react";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import {
  fmtDate, STAGES, FAITH_STATUSES, DBS_TYPES,
  LEAD_OUTCOMES, leadOutcomeLabel,
} from "../lib/constants";

interface Lead {
  id: string; org_id: string; name: string; email: string | null; phone: string | null;
  source: string | null; campaign: string | null; status: string;
  completion_outcome: string | null;
  assigned_to: string | null; assigned_name?: string | null; assigned_at: number | null;
  converted_to_person_id: string | null; person_name?: string | null;
  notes: string | null; created_at: number;
}
interface Leader { id: string; name: string; is_cdm: number; kind?: string }
interface Group { id: string; name: string }

// Pipeline tabs in order. Each maps to leads.status in the DB.
const TABS = [
  { id: "inbox",      label: "Inbox",      icon: Inbox },
  { id: "connecting", label: "Connecting", icon: MessageSquare },
  { id: "starting",   label: "Starting",   icon: Sparkles },
  { id: "active_dbs", label: "Active DBS", icon: BookOpen },
  { id: "completed",  label: "Completed",  icon: CheckCircle2 },
  { id: "closed",     label: "Closed",     icon: XCircle },
] as const;
type TabId = (typeof TABS)[number]["id"];

export default function Leads() {
  const [tab, setTab] = useState<TabId>("inbox");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [adding, setAdding] = useState(false);
  const [actingOn, setActingOn] = useState<Lead | null>(null);
  const [converting, setConverting] = useState<Lead | null>(null);

  const load = async () => {
    const [l, ld, g] = await Promise.all([
      api.get<{ leads: Lead[]; counts: { status: string; c: number }[] }>(`/leads?status=${tab}`),
      api.get<{ leaders: Leader[] }>("/leaders"),
      api.get<{ groups: Group[] }>("/groups"),
    ]);
    setLeads(l.leads);
    const map: Record<string, number> = {};
    l.counts.forEach((r: any) => { map[r.status] = r.c; });
    setCounts(map);
    setLeaders(ld.leaders);
    setGroups(g.groups);
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [tab]);

  const cdmLeaders = useMemo(() => leaders.filter((l) => l.is_cdm === 1), [leaders]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Leads</h1>
          <p className="text-ink-500 text-sm">Incoming campaign leads — assign to a Campaign Disciplemaker, then convert to a Person once contact is made.</p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" /> Add lead
        </button>
      </div>

      {/* Tabs */}
      <div className="card p-1 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const c = counts[t.id] ?? 0;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 min-w-[100px] inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
                active ? "bg-brand text-white shadow-sm" : "text-ink-600 hover:bg-ink-50"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className={`badge ${active ? "bg-white/20 text-white" : "bg-ink-100 text-ink-600"}`}>{c}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {leads.length === 0 ? (
        <div className="card p-10 text-center text-ink-500">
          {tab === "inbox" ? (
            <>No new leads in the Inbox. Campaign-generated leads appear here ready to be assigned to a Campaign Disciple Maker.</>
          ) : (
            <>No leads with this status.</>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((l) => (
            <div key={l.id} className="card p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{l.name}</span>
                    {l.campaign && <span className="badge bg-purple-100 text-purple-800">{l.campaign}</span>}
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    {l.source ?? "—"} · received {fmtDate(l.created_at)}
                  </div>
                  <div className="mt-2 text-sm text-ink-700 flex items-center gap-3 flex-wrap">
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-brand"><Phone className="h-3.5 w-3.5" />{l.phone}</a>}
                    {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-brand"><Mail className="h-3.5 w-3.5" />{l.email}</a>}
                  </div>
                  {l.assigned_name && (
                    <div className="text-xs text-ink-500 mt-2">Assigned to <span className="font-medium text-ink-700">{l.assigned_name}</span> {l.assigned_at ? `· ${fmtDate(l.assigned_at)}` : ""}</div>
                  )}
                  {l.converted_to_person_id && (
                    <div className="text-xs text-green-700 mt-2">Converted to person {l.person_name ? `· ${l.person_name}` : ""}</div>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-stretch sm:items-end">
                  {/* Active actions for live pipeline stages */}
                  {(tab === "inbox" || tab === "connecting" || tab === "starting") && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      <select
                        className="input py-1.5 text-sm w-auto"
                        value={l.assigned_to ?? ""}
                        onChange={async (e) => {
                          if (e.target.value) {
                            await api.post(`/leads/${l.id}/assign`, { leader_id: e.target.value });
                          } else {
                            await api.post(`/leads/${l.id}/unassign`);
                          }
                          load();
                        }}
                      >
                        <option value="">— Assign to CDM —</option>
                        {cdmLeaders.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        {cdmLeaders.length === 0 && <option disabled>No CDMs yet</option>}
                      </select>
                      <button
                        onClick={() => setConverting(l)}
                        className="btn-primary text-sm py-1.5 px-3"
                        disabled={!!l.converted_to_person_id}
                        title="Convert this lead into a Person record"
                      >
                        <ArrowRight className="h-4 w-4" /> Convert
                      </button>
                      <button
                        onClick={() => setActingOn(l)}
                        className="btn-secondary text-sm py-1.5 px-3"
                        title="More actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {/* Once they're in Active DBS, surface "Mark completed" with the outcome picker */}
                  {tab === "active_dbs" && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      <select
                        className="input py-1.5 text-sm w-auto"
                        defaultValue=""
                        onChange={async (e) => {
                          if (!e.target.value) return;
                          await api.patch(`/leads/${l.id}`, {
                            status: "completed",
                            completion_outcome: e.target.value,
                          });
                          load();
                        }}
                      >
                        <option value="">Mark Completed as…</option>
                        {LEAD_OUTCOMES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                      <button onClick={() => setActingOn(l)} className="btn-secondary text-sm py-1.5 px-3">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {/* Completed: outcome chip + ability to change it */}
                  {tab === "completed" && (
                    <div className="flex flex-wrap gap-2 justify-end items-center">
                      <span className={`badge ${l.completion_outcome === "multiplying" ? "bg-brand-50 text-brand-700" : "bg-green-100 text-green-800"}`}>
                        {l.completion_outcome === "multiplying" ? <Sparkles className="h-3 w-3 mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        {leadOutcomeLabel(l.completion_outcome)}
                      </span>
                      <select
                        className="input py-1.5 text-sm w-auto"
                        value={l.completion_outcome ?? ""}
                        onChange={async (e) => {
                          await api.patch(`/leads/${l.id}`, {
                            completion_outcome: e.target.value || null,
                          });
                          load();
                        }}
                      >
                        <option value="">— Outcome not set —</option>
                        {LEAD_OUTCOMES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                      <button onClick={() => setActingOn(l)} className="btn-secondary text-sm py-1.5 px-3">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {tab === "closed" && (
                    <button onClick={() => setActingOn(l)} className="btn-secondary text-sm py-1.5 px-3">
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              {l.notes && <div className="mt-3 text-xs text-ink-500 italic">{l.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <Modal open={adding} onClose={() => setAdding(false)} title="Add lead">
        <LeadForm onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      </Modal>

      <Modal
        open={!!actingOn}
        onClose={() => setActingOn(null)}
        title={actingOn?.name ?? ""}
        footer={
          <>
            <button
              className="btn-ghost text-brand"
              onClick={async () => {
                if (!actingOn) return;
                if (!confirm("Delete this lead?")) return;
                await api.del(`/leads/${actingOn.id}`);
                setActingOn(null);
                load();
              }}
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
            <button className="btn-secondary" onClick={() => setActingOn(null)}>Close</button>
          </>
        }
      >
        {actingOn && (
          <div className="space-y-3 text-sm">
            <Field label="Status">
              <select
                className="input"
                value={actingOn.status}
                onChange={async (e) => {
                  await api.patch(`/leads/${actingOn.id}`, { status: e.target.value });
                  setActingOn({ ...actingOn, status: e.target.value });
                  load();
                }}
              >
                {TABS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Notes">
              <textarea
                className="input min-h-[80px]"
                value={actingOn.notes ?? ""}
                onChange={(e) => setActingOn({ ...actingOn, notes: e.target.value })}
                onBlur={async () => { await api.patch(`/leads/${actingOn.id}`, { notes: actingOn.notes }); load(); }}
              />
            </Field>
          </div>
        )}
      </Modal>

      <Modal
        open={!!converting}
        onClose={() => setConverting(null)}
        title={`Convert ${converting?.name ?? ""} to a Person`}
        wide
      >
        {converting && (
          <ConvertForm
            lead={converting}
            leaders={leaders}
            groups={groups}
            onCancel={() => setConverting(null)}
            onConverted={() => { setConverting(null); load(); }}
          />
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function LeadForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", source: "", campaign: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try { await api.post("/leads", form); onSaved(); }
        catch (e: any) { setErr(e.message); }
        finally { setBusy(false); }
      }}
      className="space-y-3"
    >
      <Field label="Name">
        <input className="input" required autoCapitalize="words" value={form.name} onChange={(e) => set("name", e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Phone">
          <input className="input" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </Field>
        <Field label="Email">
          <input className="input" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label="Source">
          <input className="input" placeholder="e.g. Online — Campaign A" value={form.source} onChange={(e) => set("source", e.target.value)} />
        </Field>
        <Field label="Campaign">
          <input className="input" placeholder="e.g. Go Make Disciples 2026" value={form.campaign} onChange={(e) => set("campaign", e.target.value)} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea className="input min-h-[70px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
      </Field>
      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save lead"}</button>
      </div>
    </form>
  );
}

function ConvertForm({
  lead, leaders, groups, onConverted, onCancel,
}: {
  lead: Lead; leaders: Leader[]; groups: Group[];
  onConverted: () => void; onCancel: () => void;
}) {
  const [pathwayStage, setPathwayStage] = useState("invited");
  const [faithStatus, setFaithStatus] = useState("exploring");
  const [groupId, setGroupId] = useState<string>("");
  const [leaderId, setLeaderId] = useState<string>(lead.assigned_to ?? "");
  const [dbsType, setDbsType] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="space-y-4 text-sm">
      <p className="text-ink-600">
        We'll create a new <strong>Person</strong> record from this lead and link it back. The lead will move to <strong>Converted</strong>.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Pathway stage">
          <select className="input" value={pathwayStage} onChange={(e) => setPathwayStage(e.target.value)}>
            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Faith status">
          <select className="input" value={faithStatus} onChange={(e) => setFaithStatus(e.target.value)}>
            {FAITH_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Group (optional)">
          <select className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">— None —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </Field>
        <Field label="Assigned leader">
          <select className="input" value={leaderId} onChange={(e) => setLeaderId(e.target.value)}>
            <option value="">— None —</option>
            {leaders.map((l) => <option key={l.id} value={l.id}>{l.name}{l.is_cdm ? " · CDM" : ""}</option>)}
          </select>
        </Field>
        <Field label="DBS type">
          <select className="input col-span-2" value={dbsType} onChange={(e) => setDbsType(e.target.value)}>
            <option value="">— Decide later —</option>
            {DBS_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </Field>
      </div>
      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
        <button
          className="btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true); setErr(null);
            try {
              await api.post(`/leads/${lead.id}/convert`, {
                pathway_stage: pathwayStage,
                faith_status: faithStatus,
                group_id: groupId || null,
                assigned_leader_id: leaderId || null,
                dbs_type: dbsType || null,
              });
              onConverted();
            } catch (e: any) { setErr(e.message); }
            finally { setBusy(false); }
          }}
        >
          <Send className="h-4 w-4" /> {busy ? "Converting…" : "Create person & convert"}
        </button>
      </div>
    </div>
  );
}
